import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { DataTable } from 'components';
import { LikerScaleRenderContext } from 'components/DataTable/cellrenders';
import { descendingComparator } from 'components/DataTable/functions';
import {
  DefineColumn,
  ChartProps,
  SearchParams,
} from 'components/DataTable/types';
import { CompareFunction } from 'components/DataTable/types';
import { EntityType } from 'enums';
import { NetworkError, UnauthorizedError } from 'errors';
import {
  ChartTypeEnum,
  AreaEnum,
  SegmentEnum,
  FilterType,
  FilterTypeEnum,
  PaginationReturn,
} from 'generated';
import { useBootstrapApp, useDataTestId } from 'hooks';
import { generateIndicatorWithContext, indicatorQueryBuilder } from 'utils';

import { ScatterChartWrapper, AboutWrapper } from './components';
import { useStyles } from './styles';

const columnOrder = {
  entityName: 0,
  general: 1,
  elementarySchool1: 2,
  elementarySchool2: 3,
  highSchool: 4,
};

type ColumnType = keyof typeof columnOrder;

function getSegment(key: SegmentEnum): SegmentEnum {
  const result = {
    [SegmentEnum.EfI]: SegmentEnum.EfI,
    [SegmentEnum.EfIi]: SegmentEnum.EfIi,
    [SegmentEnum.Em]: SegmentEnum.Em,
  };

  return result[key];
}

function getArea(key: AreaEnum): AreaEnum {
  const result = {
    [AreaEnum.Lc]: AreaEnum.Lc,
    [AreaEnum.Ch]: AreaEnum.Ch,
    [AreaEnum.Cn]: AreaEnum.Cn,
    [AreaEnum.Mt]: AreaEnum.Mt,
  };

  return result[key];
}

function sortColumn(keyA: ColumnType, keyB: ColumnType): number {
  if (columnOrder[keyA] < columnOrder[keyB]) {
    return -1;
  }

  if (columnOrder[keyA] > columnOrder[keyB]) {
    return 1;
  }

  return 0;
}

const likertCompareFunction: CompareFunction = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, `${orderBy as string}.scaleValue`)
    : (a, b) => -descendingComparator(a, b, `${orderBy as string}.scaleValue`);
};

export const pageLimit = 15;
export const collapsedHeight = 90;
const chartProps: ChartProps<any, any> = {
  bgColors: ['#D582F9', '#0075B0'],
  levelBordered: false,
  barWidth: 10,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useBloc() {
  const classes = useStyles();
  const history = useHistory();
  const { getTestId } = useDataTestId();
  const descriptionRef = useRef<HTMLHeadingElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const {
    indicatorEvent,
    indicatorEventId,
    currentContext,
    currentIndicator,
    contexts,
    entityType,
    entity,
    updateCurrentContext,
    globalLoading,
    similarSchoolsCompare,
    updateCurrentIndicator,
    updateSimiliarSchoolsCompare,
  } = useBootstrapApp();

  const [entityNameFilter, setEntityNameFilter] = useState<
    string | undefined
  >();
  const [pagination, setPagination] = useState<PaginationReturn>({
    currentPage: 1,
    totalPage: 0,
    totalCount: 0,
    hasNextPage: false,
  });
  const [selectedFilterChartType, setSelectedFilterChartType] =
    useState<FilterTypeEnum>(FilterTypeEnum.General);
  const [error, setError] = useState<any | undefined>();
  const [area, setArea] = useState<FilterType | undefined>(() => {
    const [firstArea] = currentIndicator?.filters.area || [];

    if (firstArea) {
      return firstArea;
    }

    return undefined;
  });
  const [segment, setSegment] = useState<FilterType | undefined>(() => {
    const [firstSegment] = currentIndicator?.filters.chartsFilters || [];

    if (firstSegment) {
      return firstSegment;
    }

    return undefined;
  });
  const [descriptionIsOpen, setDescriptionIsOpen] = useState<boolean>(false);
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0);
  const [fetchPolicy, setFetchPolicy] = useState<
    'cache-first' | 'network-only'
  >('cache-first');
  const [data, setData] = useState<any>([]);
  const [info, setInfo] = useState<Record<string, any>>({});
  const [skip, setSkip] = useState<boolean>(() => {
    return (!!data.length && !!Object.keys(info).length) || !currentIndicator;
  });

  const memoIsScatter = useMemo(() => {
    return (
      currentIndicator?.chartType[selectedFilterChartType] ===
      ChartTypeEnum.Scatter
    );
  }, [currentIndicator?.chartType, selectedFilterChartType]);

  const memoDefineColumns = useMemo(() => {
    if (!memoIsScatter) {
      const fallbackFirstColumn = {
        id:
          selectedFilterChartType === FilterTypeEnum.Schools
            ? 'school'
            : 'entityName',
        label:
          selectedFilterChartType === FilterTypeEnum.Schools
            ? 'Escola'
            : 'Nome',
      };
      const isDistribution =
        currentIndicator?.chartType[selectedFilterChartType] ===
        ChartTypeEnum.Distribution;
      const [firstData] = data;
      let columns: FilterType[] = currentIndicator?.filters?.columns || [];
      const firstColumnIsSchool = !!firstData?.school;
      const firstColumnIsEntityName = !!firstData?.entityName;
      const firstColumnId = firstColumnIsSchool
        ? 'school'
        : (firstColumnIsEntityName && 'entityName') || null;
      const firstColumnName = firstColumnIsSchool
        ? 'Escola'
        : (firstColumnIsEntityName && 'Nome') || null;
      const firstColumn =
        firstColumnIsSchool || firstColumnIsEntityName
          ? [
              {
                id: firstColumnId || 'entityName',
                label: firstColumnName || 'N/A',
              },
            ]
          : [fallbackFirstColumn];
      columns = [...firstColumn, ...columns];

      return [...columns]
        .sort((columnA, columnB) =>
          sortColumn(columnA.id as ColumnType, columnB.id as ColumnType),
        )
        .reduce((accumulator: DefineColumn[], column, _, arr) => {
          const isFirstColumn =
            column.id === 'entityName' || column.id === 'school';
          const isGeneral = column.id === 'general';
          const isSortable =
            (data.length > 1 || entityType !== EntityType.SCHOOL) &&
            !isFirstColumn &&
            !isDistribution;
          const isSearchable =
            (data.length > 1 || entityType !== EntityType.SCHOOL) &&
            isFirstColumn;

          const maxColumnSize = arr.length < 3 ? 300 : 250;
          const isSchoolColumn = column.id === 'school';
          const firstColumnRender =
            isFirstColumn && isSchoolColumn ? 'schoolNameRender' : undefined;

          accumulator = [
            ...accumulator,
            {
              id: column.id,
              label: column.label ?? '--',
              cellRenderer: isFirstColumn ? firstColumnRender : 'likert',
              maxWidth: isFirstColumn ? maxColumnSize : undefined,
              hasTooltip: true,
              labelIsBold: isGeneral,
              compareFunction: currentIndicator?.chartType[
                selectedFilterChartType
              ]?.includes('likert')
                ? likertCompareFunction
                : undefined,
              sortable: isSortable,
              searchable: isSearchable,
              chartProps: !isFirstColumn ? chartProps : undefined,
            },
          ];

          return accumulator;
        }, []);
    }

    return [];
  }, [
    currentIndicator?.chartType,
    currentIndicator?.filters?.columns,
    data,
    entityType,
    memoIsScatter,
    selectedFilterChartType,
  ]);

  const memoCharType = useMemo(() => {
    return (
      currentIndicator?.chartType[selectedFilterChartType] ||
      ChartTypeEnum.Likert
    );
  }, [currentIndicator?.chartType, selectedFilterChartType]);

  const memoExpansible = useMemo(() => {
    if (!currentIndicator) {
      return false;
    }

    return (
      currentIndicator?.chartType[selectedFilterChartType] ===
        ChartTypeEnum.Distribution ||
      currentIndicator?.chartType[selectedFilterChartType] ===
        ChartTypeEnum.LikertDistribution
    );
  }, [currentIndicator, selectedFilterChartType]);

  const memoIsAbout = useMemo(() => {
    return currentContext?.id === 'about' && currentIndicator?.id === 'about';
  }, [currentContext, currentIndicator]);

  const memoShowMoreButton = useMemo(() => {
    return (
      Number(descriptionHeight) >
      (memoIsAbout ? collapsedHeight - 24 : collapsedHeight)
    );
  }, [descriptionHeight, memoIsAbout]);

  const memoIndicators = useMemo(() => {
    return generateIndicatorWithContext(contexts);
  }, [contexts]);

  const memoCurrentIndicator = useMemo(() => {
    return (
      memoIndicators.findIndex(
        indicator =>
          indicator.id === currentIndicator?.id &&
          indicator.contextId === currentContext?.id,
      ) ?? 0
    );
  }, [currentContext?.id, currentIndicator?.id, memoIndicators]);

  const memoShowSimilarSchools = useMemo(() => {
    return (
      !!currentIndicator?.filters?.hasSimilarSchools &&
      currentIndicator?.chartType !== ChartTypeEnum.Scatter &&
      entityType === EntityType.SCHOOL
    );
  }, [
    currentIndicator?.chartType,
    currentIndicator?.filters?.hasSimilarSchools,
    entityType,
  ]);

  const memoPrevIsDisabled = useMemo(() => {
    return memoCurrentIndicator === 0;
  }, [memoCurrentIndicator]);

  const memoNextIsDisabled = useMemo(() => {
    return memoCurrentIndicator === memoIndicators.length - 1;
  }, [memoCurrentIndicator, memoIndicators]);

  const memoQuery = useMemo(() => {
    const firstColumId =
      selectedFilterChartType === FilterTypeEnum.Schools &&
      entityType === EntityType.COMPANY
        ? `
          school {
            name
            brandsName
          }`
        : 'entityName';
    const options = {
      entityType,
      indicator: currentIndicator,
      similarSchoolsCompare,
      entity,
      currentFilterChartType: selectedFilterChartType,
      firstColumId,
    };
    return indicatorQueryBuilder(options);
  }, [
    entityType,
    currentIndicator,
    similarSchoolsCompare,
    entity,
    selectedFilterChartType,
  ]);

  const memoIsSchool = useMemo(() => {
    return entityType === EntityType.SCHOOL;
  }, [entityType]);

  const [getIndicatorData, { loading, data: indicatorData, client }] =
    useLazyQuery(memoQuery, {
      fetchPolicy,
      errorPolicy: 'all',
      onCompleted(newData: Record<string, any>) {
        setSkip(true);

        setInfo({
          description: currentIndicator?.description,
          name: currentIndicator?.name,
          scale: currentIndicator?.scale,
        });

        setPagination({
          ...newData.data.pagination,
          currentPage: newData?.data?.pagination?.currentPage || 1,
        });

        const formattedData = memoIsScatter
          ? newData.data
          : (newData.data.data as any[])?.map((item, index) => ({
              ...item,
              id: index,
            }));

        if (!memoIsScatter) {
          setData(formattedData);
        }

        setTimeout(() => {
          setDescriptionHeight(Number(descriptionRef.current?.scrollHeight));
          setFetchPolicy('cache-first');
        }, 100);
      },
      onError({ networkError, graphQLErrors }) {
        if (networkError) {
          setError(new NetworkError());
        }

        if (graphQLErrors.length) {
          let gqlError: Error | undefined;

          graphQLErrors.forEach(({ extensions }) => {
            const { code } = extensions || {};

            switch (code) {
              case 'INTERNAL_SERVER_ERROR':
                gqlError = new NetworkError();
                break;
              case 'FORBIDDEN':
                gqlError = new UnauthorizedError();
                break;
              default:
            }
          });

          setError(gqlError || new NetworkError());
          // TODO: implementar lógica de erros junto ao backend
          // setError(new UserNotFoundError());
        }
      },
    });

  const memoLoading = useMemo(() => {
    return loading || globalLoading;
  }, [loading, globalLoading]);

  const memoRequestVariables = useMemo(
    () => ({
      indicatorEventId,
      indicatorId: currentIndicator?.id,
      entityId: entity?.id,
      schoolId: entity?.id,
      id: entity?.id,
      area: area ? getArea(area.id as AreaEnum) : undefined,
      segment: segment ? getSegment(segment.id as SegmentEnum) : undefined,
      filterType: selectedFilterChartType,
      limit: pageLimit,
      page: pagination.currentPage,
      entityNameFilter,
    }),
    [
      area,
      currentIndicator?.id,
      entity?.id,
      entityNameFilter,
      indicatorEventId,
      pagination.currentPage,
      segment,
      selectedFilterChartType,
    ],
  );

  const handleChangeSegment = useCallback(
    (newSegment: FilterType) => {
      setSegment(newSegment);
      setSkip(false);

      getIndicatorData({
        variables: {
          ...memoRequestVariables,
          segment: getSegment(newSegment.id as SegmentEnum),
        },
      });
    },
    [getIndicatorData, memoRequestVariables],
  );

  const handleChangeFilterChartType = useCallback(
    (newFilter: FilterTypeEnum) => {
      client?.resetStore();
      setFetchPolicy('network-only');
      setSelectedFilterChartType(newFilter);
      setSkip(false);
      setPagination(oldValue => ({
        ...oldValue,
        page: 1,
      }));

      // TODO: avaliar a necessidade disso aqui
      // getIndicatorData({
      //   variables: {
      //     ...memoRequestVariables,
      //     page: 1,
      //     filterType: newFilter,
      //   },
      // });
    },
    [client],
  );

  const handleChangeArea = useCallback(
    (newArea: FilterType) => {
      setArea(newArea);
      setSkip(false);

      getIndicatorData({
        variables: {
          ...memoRequestVariables,
          area: getArea(newArea.id as AreaEnum),
        },
      });
    },
    [getIndicatorData, memoRequestVariables],
  );

  const handlePagination = useCallback((newPage: number) => {
    setSkip(false);

    setPagination(oldValue => ({
      ...oldValue,
      currentPage: newPage,
    }));
  }, []);

  const handleSearch = useCallback(
    (searchParams?: SearchParams<any>) => {
      setSkip(false);
      const [value] = Object.values(searchParams || {});

      setEntityNameFilter(value || undefined);
      setFetchPolicy('network-only');
      setData([]);
      client?.clearStore();
    },
    [client],
  );

  const RenderComponent = useMemo(() => {
    const isDistribution =
      currentIndicator?.chartType[selectedFilterChartType] ===
      ChartTypeEnum.Distribution;

    if (!memoIsAbout && !memoIsScatter) {
      return (
        <DataTable<any, LikerScaleRenderContext>
          fixedHeader
          data={memoDefineColumns.length < 2 ? [] : data}
          loading={memoLoading}
          expansible={memoExpansible}
          defineColumns={memoDefineColumns}
          loadingRowsQuantity={4}
          allExpanded={isDistribution && data.length > 1}
          onSearch={handleSearch}
          onPagination={handlePagination}
          pagination={pagination}
          limit={pageLimit}
          context={{
            scale: currentIndicator?.scale,
            chartType: memoCharType,
            similarSchoolsCompare:
              similarSchoolsCompare && memoShowSimilarSchools,
          }}
        />
      );
    }

    if (!memoIsAbout && memoIsScatter) {
      return (
        <ScatterChartWrapper
          loading={memoLoading}
          onChangeSegment={handleChangeSegment}
          data={data}
          segments={currentIndicator?.filters?.chartsFilters || undefined}
          value={segment}
          entityType={entityType}
        />
      );
    }

    return indicatorEvent ? (
      <AboutWrapper indicatorEvent={indicatorEvent} />
    ) : (
      <></>
    );
  }, [
    currentIndicator?.chartType,
    currentIndicator?.scale,
    currentIndicator?.filters?.chartsFilters,
    selectedFilterChartType,
    memoIsAbout,
    memoIsScatter,
    indicatorEvent,
    memoDefineColumns,
    data,
    memoLoading,
    memoExpansible,
    handleSearch,
    handlePagination,
    pagination,
    memoCharType,
    similarSchoolsCompare,
    memoShowSimilarSchools,
    handleChangeSegment,
    segment,
    entityType,
  ]);

  const handleToggleOpenInfo = useCallback(() => {
    setDescriptionIsOpen(oldState => !oldState);
  }, []);

  const handleChangeIndicator = useCallback(
    (type: 'next' | 'prev') => () => {
      const currentIndex = memoIndicators.findIndex(
        indicator => indicator.id === currentIndicator?.id,
      );

      if (currentIndex !== -1) {
        const newIndex = type === 'next' ? currentIndex + 1 : currentIndex - 1;

        if (newIndex < 0) {
          return;
        }

        const indicator = memoIndicators[newIndex];
        const newContext = contexts.find(
          context => context.id === indicator.contextId,
        );

        if (newContext) {
          const path =
            newContext.id === 'about'
              ? '/about'
              : `/context/${newContext.id}/indicator/${indicator.id}`;
          setSkip(true);
          updateCurrentContext(newContext);
          updateCurrentIndicator(indicator);
          history.push(
            `${path}?entityId=${entity?.id}&entityType=${entityType}&indicatorEventId=${indicatorEventId}`,
          );
        }
      }
    },
    [
      contexts,
      currentIndicator?.id,
      entity?.id,
      entityType,
      history,
      indicatorEventId,
      memoIndicators,
      updateCurrentContext,
      updateCurrentIndicator,
    ],
  );

  useEffect(() => {
    if (
      !skip &&
      currentIndicator &&
      currentIndicator?.id !== 'about' &&
      entity
    ) {
      if (memoIsScatter && !area && !segment) {
        return;
      }

      getIndicatorData({
        variables: {
          indicatorEventId,
          indicatorId: currentIndicator?.id,
          entityId: entity?.id,
          schoolId: entity?.id,
          id: entity?.id,
          area: area ? getArea(area.id as AreaEnum) : undefined,
          segment: segment ? getSegment(segment.id as SegmentEnum) : undefined,
          filterType: selectedFilterChartType,
          limit: pageLimit,
          page: pagination.currentPage,
          entityNameFilter,
        },
      });
    }

    if (
      !skip &&
      currentIndicator &&
      currentIndicator?.id === 'about' &&
      entity
    ) {
      setTimeout(() => {
        setDescriptionHeight(Number(descriptionRef.current?.scrollHeight));
      }, 500);
    }
  }, [
    indicatorEventId,
    entity,
    skip,
    currentIndicator,
    getIndicatorData,
    entityType,
    area,
    segment,
    selectedFilterChartType,
    pagination.currentPage,
    memoRequestVariables,
    entityNameFilter,
    memoIsScatter,
  ]);

  useEffect(() => {
    function handleRemoveEventListner() {
      window.removeEventListener('keyup', () => null);
    }
    window.addEventListener('keyup', event => {
      switch (event.key) {
        case 'ArrowRight':
          nextButtonRef.current?.click();
          break;
        case 'ArrowLeft':
          prevButtonRef.current?.click();
          break;
        default:
      }
    });

    return () => {
      handleRemoveEventListner();
    };
  }, []);

  useEffect(() => {
    if (!loading && !!indicatorData?.data && memoIsScatter) {
      setData(indicatorData.data);
    }
  }, [loading, indicatorData, memoIsScatter]);

  return {
    area,
    info,
    error,
    classes,
    getTestId,
    entityType,
    pagination,
    memoLoading,
    memoIsAbout,
    memoIsSchool,
    setPagination,
    memoIsScatter,
    prevButtonRef,
    nextButtonRef,
    memoIndicators,
    indicatorEvent,
    descriptionRef,
    RenderComponent,
    handleChangeArea,
    currentIndicator,
    memoDefineColumns,
    descriptionIsOpen,
    memoNextIsDisabled,
    memoPrevIsDisabled,
    memoShowMoreButton,
    memoCurrentIndicator,
    handleToggleOpenInfo,
    handleChangeIndicator,
    similarSchoolsCompare,
    memoShowSimilarSchools,
    selectedFilterChartType,
    setSelectedFilterChartType,
    handleChangeFilterChartType,
    updateSimiliarSchoolsCompare,
  };
}
